/**
 * Admin Trends Page (Spark Explore)
 *
 * Platform-staff view over every trend Spark Explore has stored — all topics
 * and freshness states, not just a user's preferred topics or the public
 * feed. Server-side paginated and filtered (search, topic/subtopic, source,
 * freshness, sort); filters are mirrored in the URL.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CaretRight as ChevronRight,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { formatDateTime } from "@/components/explore/format";
import { TrendDetailDialog } from "@/components/explore/TrendDetailDialog";
import { useExploreOverview, useExploreTrends } from "@/hooks/useExploreAdmin";
import { getErrorMessage } from "@/services/api";
import { EXPLORE_SOURCE_LABELS, isExploreSource } from "@/services/explore";
import type {
  AdminExploreTrend,
  ExploreFreshnessFilter,
  ExploreSource,
  ExploreTrendSort,
} from "@/services/explore";
import { cn } from "@/utils/cn";

const PAGE_SIZE = 25;

interface TrendFilters {
  search: string;
  topicId: string;
  subtopicId: string;
  source: "" | ExploreSource;
  freshness: ExploreFreshnessFilter;
  sort: ExploreTrendSort;
  order: "asc" | "desc";
}

const EMPTY_FILTERS: TrendFilters = {
  search: "",
  topicId: "",
  subtopicId: "",
  source: "",
  freshness: "all",
  sort: "updated",
  order: "desc",
};

function filtersFromParams(params: URLSearchParams): TrendFilters {
  const source = params.get("source");
  const freshness = params.get("freshness");
  const sort = params.get("sort");
  const order = params.get("order");
  return {
    search: params.get("search") ?? "",
    topicId: params.get("topicId") ?? "",
    subtopicId: params.get("subtopicId") ?? "",
    source: isExploreSource(source) ? source : "",
    freshness:
      freshness === "fresh" || freshness === "expired" ? freshness : "all",
    sort: sort === "popularity" ? "popularity" : "updated",
    order: order === "asc" ? "asc" : "desc",
  };
}

function paramsFromFilters(filters: TrendFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value && value !== EMPTY_FILTERS[key as keyof TrendFilters]) {
      params.set(key, value);
    }
  }
  return params;
}

const SELECT_CLASS =
  "block w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

function SourceBadges({ sources }: { sources: ExploreSource[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {sources.map((source) => (
        <Badge key={source} variant="outline">
          {EXPLORE_SOURCE_LABELS[source] ?? source}
        </Badge>
      ))}
    </span>
  );
}

function FreshnessBadge({ trend }: { trend: AdminExploreTrend }) {
  if (trend.freshness === "expired") {
    return <Badge variant="outline">Expired</Badge>;
  }
  return (
    <Badge variant={trend.feedEligible ? "success" : "warning"}>
      {trend.feedEligible ? "Fresh · feed-eligible" : "Fresh"}
    </Badge>
  );
}

export function AdminTrendsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(() => filtersFromParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchInput, setSearchInput] = useState(initialFilters.search);
  const [filters, setFilters] = useState<TrendFilters>(initialFilters);
  const [cursor, setCursor] = useState<string | undefined>();
  const [previousItems, setPreviousItems] = useState<AdminExploreTrend[]>([]);
  const [selected, setSelected] = useState<AdminExploreTrend | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const overview = useExploreOverview();
  const topics = overview.data?.topics ?? [];
  const subtopics =
    topics.find((topic) => topic.id === filters.topicId)?.subtopics ?? [];

  const applyFilters = useCallback(
    (next: TrendFilters) => {
      setFilters(next);
      setCursor(undefined);
      setPreviousItems([]);
      setSearchParams(paramsFromFilters(next), { replace: true });
    },
    [setSearchParams]
  );

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      applyFilters({ ...filters, search: value.trim() });
    }, 400);
  }

  useEffect(() => {
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  function update<K extends keyof TrendFilters>(
    key: K,
    value: TrendFilters[K]
  ) {
    const next = { ...filters, [key]: value };
    // A subtopic only makes sense under its own topic.
    if (key === "topicId") next.subtopicId = "";
    applyFilters(next);
  }

  function clearFilters() {
    setSearchInput("");
    applyFilters(EMPTY_FILTERS);
  }

  const hasActiveFilters =
    paramsFromFilters(filters).toString() !== "" || searchInput !== "";

  const trends = useExploreTrends({
    search: filters.search || undefined,
    topicId: filters.topicId || undefined,
    subtopicId: filters.subtopicId || undefined,
    source: filters.source || undefined,
    freshness: filters.freshness,
    sort: filters.sort,
    order: filters.order,
    limit: PAGE_SIZE,
    cursor,
  });
  const items = [...previousItems, ...(trends.data?.items ?? [])];

  function loadMore() {
    if (trends.data?.items) {
      setPreviousItems((prev) => [...prev, ...trends.data.items]);
    }
    setCursor(trends.data?.cursor);
  }

  function refreshAll() {
    setCursor(undefined);
    setPreviousItems([]);
    void trends.refetch();
    void overview.refetch();
  }

  const isRefreshing = trends.isFetching || overview.isFetching;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
            Stored trends
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every trend Spark Explore has collected and stored, across all
            topics.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refreshAll}
          disabled={isRefreshing}
        >
          <RefreshCw
            className={cn("mr-1.5 h-3.5 w-3.5", isRefreshing && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
        <p className="rounded-lg border border-border/60 p-3">
          <span className="font-semibold text-foreground">All stored</span> —
          every trend saved by a collection run, including expired ones.
        </p>
        <p className="rounded-lg border border-border/60 p-3">
          <span className="font-semibold text-foreground">Fresh</span> — not yet
          expired. Fresh trends are{" "}
          <span className="font-medium">feed-eligible</span>.
        </p>
        <p className="rounded-lg border border-border/60 p-3">
          <span className="font-semibold text-foreground">
            In a user's feed
          </span>{" "}
          — the feed-eligible trends in that user's chosen topics, capped per
          topic. That set is per user and is not listed here.
        </p>
      </div>

      {overview.error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {getErrorMessage(overview.error, "Failed to load Explore topics.")}
        </div>
      )}

      <section className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1 sm:col-span-2 lg:col-span-4">
            <label
              htmlFor="trend-search"
              className="text-xs font-medium text-muted-foreground"
            >
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="trend-search"
                type="search"
                value={searchInput}
                maxLength={100}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search trend name..."
                className="w-full rounded-lg border border-border bg-card py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="trend-topic"
              className="text-xs font-medium text-muted-foreground"
            >
              Topic
            </label>
            <select
              id="trend-topic"
              value={filters.topicId}
              onChange={(e) => update("topicId", e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">All topics</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="trend-subtopic"
              className="text-xs font-medium text-muted-foreground"
            >
              Subtopic
            </label>
            <select
              id="trend-subtopic"
              value={filters.subtopicId}
              disabled={!filters.topicId}
              onChange={(e) => update("subtopicId", e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="">All subtopics</option>
              {subtopics.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="trend-source"
              className="text-xs font-medium text-muted-foreground"
            >
              Source
            </label>
            <select
              id="trend-source"
              value={filters.source}
              onChange={(e) =>
                update("source", e.target.value as TrendFilters["source"])
              }
              className={SELECT_CLASS}
            >
              {/* Filters by PROVIDER, so an admin can see exactly what each
                  of Google's three collectors found. */}
              <option value="">All sources</option>
              <optgroup label="Google">
                <option value="google">SerpAPI</option>
                <option value="serper">Serper</option>
                <option value="dataforseo">DataForSEO</option>
              </optgroup>
              <option value="x">X</option>
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="trend-freshness"
              className="text-xs font-medium text-muted-foreground"
            >
              Freshness
            </label>
            <select
              id="trend-freshness"
              value={filters.freshness}
              onChange={(e) =>
                update("freshness", e.target.value as ExploreFreshnessFilter)
              }
              className={SELECT_CLASS}
            >
              <option value="all">All stored</option>
              <option value="fresh">Fresh (feed-eligible)</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="trend-sort"
              className="text-xs font-medium text-muted-foreground"
            >
              Sort by
            </label>
            <select
              id="trend-sort"
              value={`${filters.sort}:${filters.order}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split(":") as [
                  ExploreTrendSort,
                  "asc" | "desc",
                ];
                applyFilters({ ...filters, sort, order });
              }}
              className={SELECT_CLASS}
            >
              <option value="updated:desc">Last updated (newest)</option>
              <option value="updated:asc">Last updated (oldest)</option>
              <option value="popularity:desc">Popularity (highest)</option>
              <option value="popularity:asc">Popularity (lowest)</option>
            </select>
          </div>

          {hasActiveFilters && (
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {trends.isLoading && items.length === 0 ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : trends.error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
            {getErrorMessage(trends.error, "Failed to load trends.")}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            {hasActiveFilters
              ? "No stored trends match these filters."
              : "No trends have been collected yet."}
          </div>
        ) : (
          <>
            {/* Mobile: cards */}
            <ul className="space-y-2 md:hidden" aria-label="Trends">
              {items.map((trend) => (
                <li key={trend.id}>
                  <button
                    onClick={() => setSelected(trend)}
                    className="w-full space-y-2 rounded-xl border border-border bg-card p-4 text-left hover:bg-muted/40"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="break-words font-medium">
                        {trend.title}
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {trend.topic} › {trend.subtopic}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <FreshnessBadge trend={trend} />
                      <SourceBadges sources={trend.sources} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {trend.counts.queries} queries · {trend.counts.events}{" "}
                      events · popularity {trend.popularity} · updated{" "}
                      {formatDateTime(trend.fetchedAt)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            {/* md+: table */}
            <div className="hidden overflow-x-auto rounded-xl border border-border md:block">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2.5">Trend</th>
                    <th className="px-3 py-2.5">Topic / Subtopic</th>
                    <th className="px-3 py-2.5 text-right">Queries</th>
                    <th className="px-3 py-2.5 text-right">Events</th>
                    <th className="px-3 py-2.5">Sources</th>
                    <th className="px-3 py-2.5 text-right">Popularity</th>
                    <th className="px-3 py-2.5">First seen</th>
                    <th className="px-3 py-2.5">Last updated</th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {items.map((trend) => (
                    <tr
                      key={trend.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setSelected(trend)}
                    >
                      <td className="max-w-xs px-3 py-2.5">
                        <button
                          className="text-left font-medium hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(trend);
                          }}
                        >
                          {trend.title}
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        <div>{trend.topic}</div>
                        <div className="text-muted-foreground">
                          {trend.subtopic}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {trend.counts.queries}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {trend.counts.events}
                      </td>
                      <td className="px-3 py-2.5">
                        <SourceBadges sources={trend.sources} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {trend.popularity}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                        {formatDateTime(trend.firstSeenAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                        {formatDateTime(trend.fetchedAt)}
                      </td>
                      <td className="px-3 py-2.5">
                        <FreshnessBadge trend={trend} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Showing {items.length} trend{items.length === 1 ? "" : "s"}
              </p>
              {trends.data?.hasMore && trends.data.cursor && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={trends.isFetching}
                >
                  {trends.isFetching ? "Loading…" : "Load more"}
                </Button>
              )}
            </div>
          </>
        )}
      </section>

      <TrendDetailDialog
        trend={selected}
        onOpenChange={(open) => !open && setSelected(null)}
      />
    </div>
  );
}
